import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Snackbar,
  Alert,
  Paper,
  Card,
  Tabs,
  Tab,
  Chip,
  Typography,
  Button,
} from '@mui/material';
import {
  Replay as ReplayIcon,
  ErrorOutline as ErrorIcon,
  CloudUpload as CloudUploadIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import TranscricaoVincularPasta from '../components/TranscricaoVincularPasta';
import TranscricaoFila from '../components/TranscricaoFila';
import TranscricaoResultados from '../components/TranscricaoResultados';
import TranscricaoPainel from '../components/TranscricaoPainel';
import { colors } from '../theme/expertiseTheme';
import {
  iniciarTranscricaoLote,
  listarJobsAtivos,
  obterJobTranscricao,
  listarJobsTranscricao,
  cancelarTranscricao,
  baixarTranscricao,
  exportarLote,
  obterResumoTranscricoes,
  listarHistoricoPastas,
  listarProjetos,
  extrairLinks,
  isGoogleDriveFolderUrl,
  isJobAtivo,
  isJobEmAndamento,
  isJobNaFila,
  isJobFinalizado,
  nomeLote,
  ApiError,
  ArquivoTranscricao,
  JobTranscricao,
  FiltrosPainel,
  PastaTranscrita,
  ResumoTranscricoes,
  FILTROS_PAINEL_PADRAO,
  EXTENSOES_AUDIO_ACEITAS,
  MAX_LINKS_POR_LOTE,
} from '../services/transcricao';

const INTERVALO_POLLING = 5000;
const INTERVALO_POLLING_PENDENTE = 3000;
const MAX_FALHAS_POLLING = 3;
const LIMITE_JOBS_RESTAURACAO = 10;

const ABA_NOVA = 0;
const ABA_RESULTADOS = 1;

const ordenarPorCriacao = (jobs: JobTranscricao[]) =>
  [...jobs].sort((a, b) => a.criadoEm.localeCompare(b.criadoEm));

/**
 * Ferramenta de uso comum: transcrição automática de áudios de pastas do Google Drive.
 * Várias pastas podem ser enviadas de uma vez; o servidor processa algumas em paralelo e
 * mantém as demais numa fila, que anda sozinha conforme as transcrições terminam.
 */
const TranscricaoPage: React.FC = () => {
  const [abaAtiva, setAbaAtiva] = useState(ABA_NOVA);
  const [linksTexto, setLinksTexto] = useState('');
  const [erroCampo, setErroCampo] = useState('');
  const [ativos, setAtivos] = useState<JobTranscricao[]>([]);
  const [finalizados, setFinalizados] = useState<JobTranscricao[]>([]);
  const [resultadoId, setResultadoId] = useState<string | null>(null);
  const [expandidoId, setExpandidoId] = useState<string | null>(null);
  const [iniciando, setIniciando] = useState(false);
  const [cancelandoId, setCancelandoId] = useState<string | null>(null);
  const [baixando, setBaixando] = useState(false);
  const [erroJob, setErroJob] = useState('');
  const [filtrosPainel, setFiltrosPainel] = useState<FiltrosPainel>(FILTROS_PAINEL_PADRAO);
  const [resumo, setResumo] = useState<ResumoTranscricoes | null>(null);
  const [historico, setHistorico] = useState<PastaTranscrita[]>([]);
  const [projetos, setProjetos] = useState<string[]>([]);
  const [loadingPainel, setLoadingPainel] = useState(false);
  const [refreshPainel, setRefreshPainel] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'info' | 'success' | 'warning' | 'error';
  }>({ open: false, message: '', severity: 'info' });

  const falhasPollingRef = useRef(0);
  const [tentativaPolling, setTentativaPolling] = useState(0);

  const emProcessamento = useMemo(() => ativos.filter(isJobEmAndamento), [ativos]);
  const naFila = useMemo(() => ativos.filter(isJobNaFila), [ativos]);
  const haAtivos = ativos.length > 0;

  const jobResultado = useMemo(
    () => finalizados.find((j) => j.id === resultadoId) ?? finalizados[0] ?? null,
    [finalizados, resultadoId]
  );

  const mostrarNotificacao = useCallback(
    (message: string, severity: 'info' | 'success' | 'warning' | 'error' = 'info') => {
      setSnackbar({ open: true, message, severity });
    },
    []
  );

  /**
   * Avisa o usuário e leva para os resultados quando um job chega a um estado final
   */
  const notificarFinal = useCallback(
    (finalizado: JobTranscricao) => {
      const lote = nomeLote(finalizado);
      if (finalizado.status === 'Concluido') {
        if (!finalizado.totalArquivos) {
          mostrarNotificacao(
            `${lote}: nenhum arquivo de áudio (${EXTENSOES_AUDIO_ACEITAS}) encontrado`,
            'info'
          );
        } else if (finalizado.arquivosComErro > 0) {
          mostrarNotificacao(
            `${lote}: ${finalizado.arquivosConcluidos} transcrição(ões) concluída(s), ${finalizado.arquivosComErro} com erro`,
            'warning'
          );
        } else {
          mostrarNotificacao(
            `✅ ${finalizado.arquivosConcluidos} transcrição(ões) concluída(s)`,
            'success'
          );
        }
      } else if (finalizado.status === 'Erro') {
        mostrarNotificacao(`${lote}: ${finalizado.erro || 'Erro ao processar a transcrição'}`, 'error');
      } else if (finalizado.status === 'Cancelado') {
        mostrarNotificacao(`${lote}: transcrição cancelada`, 'warning');
      }
      setAbaAtiva(ABA_RESULTADOS);
      setRefreshPainel((valor) => valor + 1);
    },
    [mostrarNotificacao]
  );

  /** Guarda um job finalizado nos resultados da sessão e o seleciona */
  const registrarFinal = useCallback(
    (finalizado: JobTranscricao) => {
      setFinalizados((atuais) => [finalizado, ...atuais.filter((j) => j.id !== finalizado.id)]);
      setResultadoId(finalizado.id);
      notificarFinal(finalizado);
    },
    [notificarFinal]
  );

  /** Mescla jobs recém-criados/atualizados na lista de ativos, mantendo a ordem de criação */
  const mesclarAtivos = useCallback((novos: JobTranscricao[]) => {
    setAtivos((atuais) => {
      const porId = new Map(atuais.map((j) => [j.id, j]));
      novos.forEach((j) => porId.set(j.id, j));
      return ordenarPorCriacao(Array.from(porId.values()).filter(isJobAtivo));
    });
  }, []);

  // Retoma o acompanhamento do que ainda está rodando ou na fila (ex.: após F5) e o último resultado
  useEffect(() => {
    let ativo = true;
    listarJobsAtivos()
      .then((jobs) => {
        if (ativo) setAtivos(ordenarPorCriacao(jobs.filter(isJobAtivo)));
      })
      .catch((error) => console.error('Erro ao restaurar transcrições em andamento:', error));
    listarJobsTranscricao(LIMITE_JOBS_RESTAURACAO)
      .then((jobs) => {
        const ultimos = jobs.filter(isJobFinalizado);
        if (ativo && ultimos.length) setFinalizados((atuais) => (atuais.length ? atuais : ultimos));
      })
      .catch((error) => console.error('Erro ao carregar últimas transcrições:', error));
    return () => {
      ativo = false;
    };
  }, []);

  // Com um único job em processamento, mostra os detalhes direto
  useEffect(() => {
    if (emProcessamento.length === 1) setExpandidoId(emProcessamento[0].id);
    else if (expandidoId && !emProcessamento.some((j) => j.id === expandidoId)) setExpandidoId(null);
  }, [emProcessamento, expandidoId]);

  // Polling enquanto houver algo ativo; só desiste após falhas seguidas
  useEffect(() => {
    if (!haAtivos || erroJob) return;

    let vivo = true;
    const idsAnteriores = ativos.map((j) => j.id);
    const intervalo = ativos.some((j) => j.status === 'Pendente')
      ? INTERVALO_POLLING_PENDENTE
      : INTERVALO_POLLING;

    const timer = setTimeout(async () => {
      try {
        const atuais = await listarJobsAtivos();
        if (!vivo) return;
        falhasPollingRef.current = 0;

        // Quem sumiu da lista terminou fora deste ciclo (callback/sincronizador): busca o estado final
        const idsAtuais = new Set(atuais.map((j) => j.id));
        const sumidos = idsAnteriores.filter((id) => !idsAtuais.has(id));
        const recuperados = await Promise.all(
          sumidos.map((id) => obterJobTranscricao(id).catch(() => null))
        );
        if (!vivo) return;

        setAtivos(ordenarPorCriacao(atuais.filter(isJobAtivo)));
        [...atuais, ...recuperados]
          .filter((j): j is JobTranscricao => Boolean(j) && isJobFinalizado(j))
          .forEach(registrarFinal);
      } catch (error: any) {
        if (!vivo) return;
        console.error('Erro ao atualizar status das transcrições:', error);
        falhasPollingRef.current += 1;
        if (falhasPollingRef.current >= MAX_FALHAS_POLLING) {
          setErroJob(error.message || 'Erro ao acompanhar o processamento');
        } else {
          setTentativaPolling((valor) => valor + 1);
        }
      }
    }, intervalo);

    return () => {
      vivo = false;
      clearTimeout(timer);
    };
  }, [ativos, haAtivos, erroJob, tentativaPolling, registrarFinal]);

  // Projetos disponíveis para o filtro do painel
  useEffect(() => {
    listarProjetos()
      .then(setProjetos)
      .catch((error) => console.error('Erro ao carregar projetos:', error));
  }, []);

  // Indicadores e histórico de pastas transcritas
  useEffect(() => {
    let ativo = true;

    const carregarPainel = async () => {
      setLoadingPainel(true);
      try {
        const [resumoAtual, historicoAtual] = await Promise.all([
          obterResumoTranscricoes(filtrosPainel),
          listarHistoricoPastas(filtrosPainel),
        ]);
        if (!ativo) return;
        setResumo(resumoAtual);
        setHistorico(historicoAtual);
      } catch (error: any) {
        if (!ativo) return;
        console.error('Erro ao carregar painel de transcrições:', error);
        setResumo(null);
        setHistorico([]);
      } finally {
        if (ativo) setLoadingPainel(false);
      }
    };

    carregarPainel();

    return () => {
      ativo = false;
    };
  }, [filtrosPainel, refreshPainel]);

  const handleIniciar = async () => {
    const links = extrairLinks(linksTexto);

    if (!links.length) {
      setErroCampo('Informe o link de pelo menos uma pasta do Google Drive');
      return;
    }
    if (links.length > MAX_LINKS_POR_LOTE) {
      setErroCampo(`Envie no máximo ${MAX_LINKS_POR_LOTE} links por vez`);
      return;
    }

    const validos = links.filter(isGoogleDriveFolderUrl);
    const invalidosLocais = links.length - validos.length;
    if (!validos.length) {
      setErroCampo(
        links.length === 1
          ? 'Link inválido. Use o link de uma pasta do Google Drive.'
          : 'Nenhum link válido. Use links de pastas do Google Drive.'
      );
      return;
    }

    setErroCampo('');
    setErroJob('');
    setIniciando(true);

    try {
      const resultado = await iniciarTranscricaoLote(validos);
      falhasPollingRef.current = 0;

      mesclarAtivos(resultado.jobs);
      resultado.jobs.filter(isJobFinalizado).forEach(registrarFinal);

      const iniciados = resultado.jobs.filter(isJobEmAndamento).length;
      const enfileirados = resultado.jobs.filter(isJobNaFila).length;
      const invalidos = invalidosLocais + resultado.invalidos.length;

      const partes: string[] = [];
      if (iniciados) partes.push(`${iniciados} pasta${iniciados === 1 ? '' : 's'} em processamento`);
      if (enfileirados) partes.push(`${enfileirados} na fila`);
      if (invalidos) partes.push(`${invalidos} link${invalidos === 1 ? '' : 's'} ignorado${invalidos === 1 ? '' : 's'}`);
      mostrarNotificacao(partes.join(', ') || 'Processamento iniciado', invalidos ? 'warning' : 'info');

      setLinksTexto('');
      setAbaAtiva(ABA_NOVA);
    } catch (error: any) {
      console.error('Erro ao iniciar transcrição:', error);
      mostrarNotificacao(error.message || 'Erro ao iniciar a transcrição', 'error');
    } finally {
      setIniciando(false);
    }
  };

  const handleCancelar = async (job: JobTranscricao) => {
    setCancelandoId(job.id);
    try {
      await cancelarTranscricao(job.id);
      setAtivos((atuais) => atuais.filter((j) => j.id !== job.id));
      mostrarNotificacao(
        isJobNaFila(job) ? `${nomeLote(job)} removida da fila` : 'Transcrição cancelada',
        'warning'
      );
    } catch (error: any) {
      console.error('Erro ao cancelar transcrição:', error);
      if (error instanceof ApiError && error.status === 409) {
        // O job terminou antes do cancelamento chegar: mostra o resultado final
        try {
          const finalizado = await obterJobTranscricao(job.id);
          setAtivos((atuais) => atuais.filter((j) => j.id !== job.id));
          if (isJobFinalizado(finalizado)) registrarFinal(finalizado);
        } catch {
          mostrarNotificacao(error.message, 'warning');
        }
      } else {
        mostrarNotificacao(error.message || 'Erro ao cancelar a transcrição', 'error');
      }
    } finally {
      setCancelandoId(null);
    }
  };

  const handleBaixarArquivo = async (arquivo: ArquivoTranscricao) => {
    if (!jobResultado) return;

    setBaixando(true);
    try {
      await baixarTranscricao(jobResultado.id, arquivo);
      mostrarNotificacao(`Transcrição de ${arquivo.nome} baixada`, 'success');
    } catch (error: any) {
      console.error('Erro ao baixar transcrição:', error);
      mostrarNotificacao(error.message || 'Erro ao baixar a transcrição', 'error');
    } finally {
      setBaixando(false);
    }
  };

  const handleExportarLote = async () => {
    if (!jobResultado) return;

    setBaixando(true);
    try {
      await exportarLote(jobResultado);
      mostrarNotificacao('Exportação do lote concluída', 'success');
    } catch (error: any) {
      console.error('Erro ao exportar lote:', error);
      mostrarNotificacao(error.message || 'Erro ao exportar as transcrições do lote', 'error');
    } finally {
      setBaixando(false);
    }
  };

  const handleNovaTranscricao = () => {
    setErroCampo('');
    setAbaAtiva(ABA_NOVA);
  };

  const handleRetomarAcompanhamento = () => {
    falhasPollingRef.current = 0;
    setErroJob('');
  };

  /**
   * Aba de envio: formulário, acompanhamento (processando + fila) ou erro
   */
  const renderAbaNova = () => {
    if (erroJob) {
      return (
        <Paper
          elevation={0}
          sx={{
            maxWidth: 560,
            mx: 'auto',
            p: 5,
            textAlign: 'center',
            border: `1px solid ${colors.border}`,
            borderRadius: '16px',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <ErrorIcon sx={{ fontSize: 32, color: colors.error }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 1 }}>
            Não foi possível acompanhar o processamento
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
            {erroJob}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={handleRetomarAcompanhamento}
          >
            Tentar novamente
          </Button>
        </Paper>
      );
    }

    if (haAtivos) {
      return (
        <Box>
          <TranscricaoFila
            jobs={ativos}
            onCancelar={handleCancelar}
            cancelandoId={cancelandoId}
            expandidoId={expandidoId}
            onExpandir={setExpandidoId}
          />
          <TranscricaoVincularPasta
            compacto
            links={linksTexto}
            onChange={(valor) => {
              setLinksTexto(valor);
              if (erroCampo) setErroCampo('');
            }}
            onIniciar={handleIniciar}
            loading={iniciando}
            erroCampo={erroCampo}
          />
        </Box>
      );
    }

    return (
      <TranscricaoVincularPasta
        links={linksTexto}
        onChange={(valor) => {
          setLinksTexto(valor);
          if (erroCampo) setErroCampo('');
        }}
        onIniciar={handleIniciar}
        loading={iniciando}
        erroCampo={erroCampo}
      />
    );
  };

  /**
   * Aba de resultados: lotes transcritos nesta sessão, indicadores e histórico de pastas
   */
  const renderAbaResultados = () => {
    if (jobResultado) {
      return (
        <Box>
          {finalizados.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {finalizados.map((job) => (
                <Chip
                  key={job.id}
                  label={nomeLote(job)}
                  onClick={() => setResultadoId(job.id)}
                  color={job.id === jobResultado.id ? 'primary' : 'default'}
                  variant={job.id === jobResultado.id ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 500 }}
                />
              ))}
            </Box>
          )}
          <TranscricaoResultados
            key={jobResultado.id}
            job={jobResultado}
            onBaixarArquivo={handleBaixarArquivo}
            onExportarLote={handleExportarLote}
            onNovaTranscricao={handleNovaTranscricao}
            baixando={baixando}
            filtrosPainel={filtrosPainel}
            onFiltrosPainelChange={setFiltrosPainel}
            projetos={projetos}
            resumo={resumo}
            historico={historico}
            loadingPainel={loadingPainel}
          />
        </Box>
      );
    }

    return (
      <Box>
        <TranscricaoPainel
          filtros={filtrosPainel}
          onChange={setFiltrosPainel}
          projetos={projetos}
          resumo={resumo}
          historico={historico}
          loading={loadingPainel}
        />

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 5,
            textAlign: 'center',
            border: `1px solid ${colors.border}`,
            borderRadius: '16px',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '16px',
              backgroundColor: 'rgba(0, 99, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 28, color: colors.primary }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.dark, mb: 1 }}>
            {haAtivos ? 'Transcrições em andamento' : 'Nenhum lote transcrito nesta sessão'}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.text.secondary, mb: 3 }}>
            {haAtivos
              ? 'Assim que cada pasta terminar, as transcrições aparecem aqui.'
              : 'Vincule uma ou mais pastas do Google Drive para transcrever seus áudios.'}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setAbaAtiva(ABA_NOVA)}
          >
            {haAtivos ? 'Acompanhar processamento' : 'Nova transcrição'}
          </Button>
        </Paper>
      </Box>
    );
  };

  const rotuloChipAba = (() => {
    if (!haAtivos) return null;
    if (emProcessamento.length === 1 && naFila.length === 0) {
      return `${Math.round(emProcessamento[0].progresso || 0)}%`;
    }
    const partes = [];
    if (emProcessamento.length) partes.push(`${emProcessamento.length} ativa${emProcessamento.length === 1 ? '' : 's'}`);
    if (naFila.length) partes.push(`${naFila.length} na fila`);
    return partes.join(' · ');
  })();

  return (
    <Layout
      title="Transcrição de Áudios"
      subtitle="Transcreva automaticamente os áudios de pastas do Google Drive"
    >
      {/* Topbar de navegação da área */}
      <Card sx={{ mb: 3, '&:hover': { borderColor: colors.border, transform: 'none' } }}>
        <Tabs
          value={abaAtiva}
          onChange={(_, valor) => setAbaAtiva(valor)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${colors.border}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 56,
            },
            '& .Mui-selected': {
              color: colors.primary,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: colors.primary,
            },
          }}
        >
          <Tab
            icon={<CloudUploadIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Nova Transcrição
                {rotuloChipAba && (
                  <Chip
                    label={rotuloChipAba}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.6875rem',
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      color: colors.info,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            }
          />
          <Tab
            icon={<AssessmentIcon />}
            iconPosition="start"
            label="Transcrições e Histórico"
          />
        </Tabs>
      </Card>

      {abaAtiva === ABA_RESULTADOS && jobResultado?.status === 'Erro' && jobResultado.erro && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {jobResultado.erro}
        </Alert>
      )}

      {abaAtiva === ABA_RESULTADOS && jobResultado?.status === 'Cancelado' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Transcrição cancelada. Arquivos já concluídos foram gravados.
        </Alert>
      )}

      {abaAtiva === ABA_NOVA ? renderAbaNova() : renderAbaResultados()}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default TranscricaoPage;
